import {
    Button,
    Divider,
    TextField,
    Typography,
    Box,
    IconButton,
    Paper,
    Dialog,
    DialogTitle,
    DialogContent
} from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import GetAppIcon from '@material-ui/icons/GetApp'
import SendIcon from '@material-ui/icons/Send'
import AttachFileIcon from '@material-ui/icons/AttachFile'
import ReplyIcon from '@material-ui/icons/Reply'
import FileCopyIcon from '@material-ui/icons/FileCopy'
import CloseIcon from '@material-ui/icons/Close'
import QRCode from 'qrcode'
import React, { useState, useEffect, useRef } from 'react'
import { useRecoilValue } from 'recoil'
import { userInfoState } from '../recoil/atoms'
import api from '../api/client';
import { toast } from '../api/toast'
import { gregorianToJalali, toFarsiDigits } from '../assets/utils'

const useStyles = makeStyles((theme) => ({
    chatContainer: {
        flex: 1,
        minHeight: 0,
        overflowY: 'auto',
        padding: theme.spacing(2),
        backgroundColor: '#f5f5f5',
        borderRadius: '8px',
    },
    messageWrapper: {
        marginBottom: theme.spacing(2),
        display: 'flex',
        flexDirection: 'column',
    },
    messageBubble: {
        padding: theme.spacing(1.5),
        borderRadius: '12px',
        maxWidth: '70%',
        wordWrap: 'break-word',
    },
    myMessage: {
        alignSelf: 'flex-start',
        backgroundColor: '#1976d2',
        color: 'white',
        borderBottomLeftRadius: '4px',
    },
    theirMessage: {
        alignSelf: 'flex-end',
        backgroundColor: 'white',
        color: 'black',
        borderBottomRightRadius: '4px',
    },
    messageTime: {
        fontSize: '0.75rem',
        color: '#666',
        marginTop: theme.spacing(0.5),
    },
    inputArea: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1),
        marginTop: theme.spacing(2),
    },
    attachmentPreview: {
        padding: theme.spacing(1),
        backgroundColor: '#e3f2fd',
        borderRadius: '4px',
        marginBottom: theme.spacing(1),
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
    },
    statusChip: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: 4,
        fontSize: 10.5,
        fontWeight: 800,
        borderRadius: 999,
        padding: '1px 8px',
        whiteSpace: 'nowrap',
    },
    statusSent: { background: '#eceff1', color: '#607d8b' },
    statusDelivered: { background: '#e3f2fd', color: '#1565c0' },
    statusSeen: { background: '#e8f5e9', color: '#2e7d32' },
    emptyBox: {
        display: 'flex', flexDirection: 'column', alignItems: 'center',
        justifyContent: 'center', height: '100%', color: '#8a92b2', gap: 8,
    },
    msgAction: {
        textTransform: 'none', fontSize: 10.5, minWidth: 0, padding: '2px 6px',
        color: '#5c6bc0', fontWeight: 700,
    },
    replyBar: {
        display: 'flex', alignItems: 'center', gap: 8, marginTop: 8,
        background: '#eef2ff', border: '1px solid #c5cae9', borderRight: '4px solid #3949ab',
        borderRadius: 8, padding: '6px 10px',
    },
}))

// پیامِ پاسخ با «↩ پاسخ به …:» و خطوطِ «>» شروع می‌شود؛ نقلِقول را جدا می‌کنیم
// تا در حباب، فونت و پس‌زمینهٔ متفاوتی از متنِ خودِ پاسخ داشته باشد.
const splitQuote = (raw) => {
  const text = String(raw || '')
  // پیام‌ها ممکن است با CRLF ذخیره شده باشند (متنِ ارسالی از textarea)
  const m = /^↩ پاسخ به ([^\n:]*):\r?\n((?:>[^\n]*\r?\n?)*)/.exec(text)
  if (!m) return { quote: null, body: text }
  const quoteText = m[2]
    .split('\n')
    .filter((l) => l.trim().startsWith('>'))
    .map((l) => l.replace(/^>\s?/, '').replace(/\r$/, ''))
    .join('\n')
    .trim()
  if (!quoteText) return { quote: null, body: text }
  return {
    quote: { name: (m[1] || '').trim(), text: quoteText },
    body: text.slice(m[0].length).replace(/^\n+/, ''),
  }
}

/**
 * رشتهٔ گفتگو (حلقهٔ پیام‌ها + ارسال) — بدونِ دیالوگ، برای استفاده در «مرکز پیام».
 *
 * props:
 *   contractId : شناسهٔ قرارداد (null ⇒ گفتگوی بدونِ قرارداد)
 *   partnerId  : شناسهٔ طرفِ مقابل
 *   subject    : موضوعِ گفتگو
 *   onNewMessage : (msg) => void
 *   headerExtra : محتوای اختیاریِ بالای حلقه
 */
// وضعیتِ پیام ⇒ برچسبِ رنگی (ارسال شده / رسیده / رویت شده)
const STATUS_META = {
  sent: { label: 'ارسال شده', cls: 'statusSent' },
  delivered: { label: 'رسیده', cls: 'statusDelivered' },
  seen: { label: 'رویت شده', cls: 'statusSeen' },
}

const statusOf = (msg, isMy) => {
  if (msg.read_datetime) return 'seen'
  if (isMy) return msg.delivered_datetime ? 'delivered' : 'sent'
  return 'delivered'
}

const MessageThread = props => {
    const classes = useStyles()
    const userInfo = useRecoilValue(userInfoState)
    const [messages, setMessages] = useState([])
    const [newMessage, setNewMessage] = useState('')
    const [attachment, setAttachment] = useState(null)
    const [fileName, setFileName] = useState('')
    const [revealed, setRevealed] = useState({})   // پیام‌هایی که کاربر خودش باز کرده
    const [replyTo, setReplyTo] = useState(null)   // {id, name, text} — پاسخ به همین پیام
    const [loading, setLoading] = useState(true)
    const [qrUrl, setQrUrl] = useState('')
    const [qrImg, setQrImg] = useState('')
    const chatEndRef = useRef(null)

    useEffect(() => {
        let alive = true
        if (!qrUrl) { setQrImg(''); return () => { alive = false } }
        QRCode.toDataURL(qrUrl, { width: 280, margin: 1 })
            .then((d) => { if (alive) setQrImg(d) })
            .catch(() => { if (alive) setQrImg('') })
        return () => { alive = false }
    }, [qrUrl])

    // پیمانکارِ بدون اشتراک: ارسال پیام مجاز نیست
    const subLocked = !!userInfo && userInfo.role === 'Contractor'
        && userInfo.is_subscribed === false

    const contractId = props.contractId || ''
    const partnerId = props.partnerId
    const subject = props.subject || ''

    // باز کردنِ پیام: همان لحظه «زمان رویت» ثبت می‌شود
    const openMessage = async (id) => {
        setRevealed((r) => ({ ...r, [id]: true }))
        try { await api.put(`messages/${id}`) } catch (e) { /* ignore */ }
    }

    const fetchMessages = () => {
        if (!partnerId || !subject) return
        api.get(`messages/${partnerId}?contract_id=${encodeURIComponent(contractId)}&subject=${encodeURIComponent(subject)}` + (props.viewAsUser ? `&actor=${props.viewAsUser}` : ''))
            .then((response) => {
                if (response.status === 200) {
                    // API به‌صورت نزولی برمی‌گرداند (جدیدترین اول)؛
                    // در چت باید زمانی باشد ⇒ قدیمی بالا، جدید پایین
                    const rows = (response.data || []).filter((m) => m.subject === subject)
                    setMessages([...rows].reverse())
                }
            })
            .catch(() => {})
            .finally(() => setLoading(false))
    }

    useEffect(() => {
        setLoading(true)
        setRevealed({})
        fetchMessages()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [contractId, partnerId, subject])

    // ادمین پیام‌ها را بدونِ کلیک (و بدونِ blur) می‌بیند؛ ثبتِ «زمان رویت» برای او
    // سمتِ سرور و در همان GET انجام می‌شود (فقط پیام‌های خطاب به خودش).

    // ── به‌روزرسانی هوشمند (همان سیاستِ مرکز پیامِ ارجان فیش)
    const versionRef = useRef('')
    const typingRef = useRef(false)
    useEffect(() => {
        typingRef.current = String(newMessage || '').length > 0
    }, [newMessage])

    useEffect(() => {
        if (!partnerId || !subject) return undefined
        let stopped = false
        let timer = null
        let idle = 0

        const schedule = (sec) => {
            if (!stopped) timer = setTimeout(tick, sec * 1000)
        }

        const tick = async () => {
            if (stopped) return
            if (document.hidden) { schedule(60); return }
            if (typingRef.current) { schedule(15); return }
            try {
                const url = `messages/updates/?with=${partnerId}`
                    + `&contract_id=${encodeURIComponent(contractId)}`
                    + `&subject=${encodeURIComponent(subject)}`
                    + `&v=${encodeURIComponent(versionRef.current)}`
                const r = await api.get(url)
                const d = r.data || {}
                if (d.version) versionRef.current = d.version
                if (d.changed && Array.isArray(d.messages)) {
                    // API نزولی می‌دهد ⇒ برای چت معکوسش می‌کنیم (قدیمی بالا)
                    setMessages([...d.messages.filter((m) => m.subject === subject)].reverse())
                    idle = 0
                } else {
                    idle += 1
                }
            } catch (e) { /* ignore */ }
            schedule(idle >= 8 ? 60 : 15)
        }

        const onVis = () => {
            if (!document.hidden) { if (timer) clearTimeout(timer); tick() }
        }
        document.addEventListener('visibilitychange', onVis)
        schedule(15)

        return () => {
            stopped = true
            if (timer) clearTimeout(timer)
            document.removeEventListener('visibilitychange', onVis)
        }
    }, [contractId, partnerId, subject])

    // اسکرول به آخرین پیام
    useEffect(() => {
        chatEndRef.current?.scrollIntoView({ behavior: 'smooth' })
    }, [messages])

    const handleFileChange = (event) => {
        const file = event.target.files[0]
        if (file) {
            setAttachment(file)
            setFileName(file.name)
        }
    }

    const handleSendMessage = () => {
        if (!newMessage.trim() && !attachment) return

        // «پاسخ به پیام» ⇒ نقلِ پیامِ مرجع در ابتدای متن (مثلِ مرکز پیام فیش دستمزد)
        const quote = replyTo
            ? `↩ پاسخ به ${replyTo.name}:\n> ${String(replyTo.text || '').split('\n').join('\n> ')}\n\n`
            : ''
        const bodyText = quote + newMessage

        const formData = new FormData()
        if (contractId) formData.append('contract_id', contractId)
        formData.append('receiver_id', partnerId)
        formData.append('subject', subject)
        formData.append('message', bodyText)
        if (attachment) formData.append('attachment', attachment)

        api.post('messages/', formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
        }).then((response) => {
            if (response.status === 201) {
                setMessages((prev) => [...prev, response.data])
                setNewMessage('')
                setAttachment(null)
                setFileName('')
                setReplyTo(null)
                if (props.onNewMessage) props.onNewMessage(response.data)
            }
        }).catch((error) => {
            alert(error.response?.data?.error || 'خطا در ارسال پیام')
        })
    }

    // کپیِ متنِ پیام (با fallback برای مرورگرهای قدیمی)
    const copyMessage = (text) => {
        const value = String(text || '')
        const fallback = () => {
            try {
                const ta = document.createElement('textarea')
                ta.value = value
                ta.style.position = 'fixed'
                ta.style.opacity = '0'
                document.body.appendChild(ta)
                ta.select()
                document.execCommand('copy')
                document.body.removeChild(ta)
                toast('کپی شد ✓', 'success')
            } catch (e) {
                toast('کپی ممکن نشد', 'error')
            }
        }
        if (navigator.clipboard && navigator.clipboard.writeText) {
            navigator.clipboard.writeText(value).then(() => toast('کپی شد ✓', 'success')).catch(fallback)
        } else {
            fallback()
        }
    }

    const handleKeyPress = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault()
            handleSendMessage()
        }
    }

    return (
        <div style={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}>
            {props.headerExtra}

            {/* منطقه نمایش پیام‌ها */}
            <Paper className={classes.chatContainer} elevation={0}>
                {messages.length === 0 ? (
                    <div className={classes.emptyBox}>
                        <Typography color="textSecondary">
                            {loading ? 'در حال بارگذاری…' : 'پیامی موجود نیست'}
                        </Typography>
                    </div>
                ) : (
                    messages.map((msg, index) => {
                        const isMyMessage = msg.sender === -1
                        // ادمین همهٔ پیام‌ها را می‌بیند (بدون ثبتِ رویت و بدون علامتِ خوانده‌شدن)
                        const isAdmin = userInfo && userInfo.role === 'Admin'
                        // پیامِ خوانده‌نشده تا کلیکِ کاربر محو (blur) می‌ماند
                        const locked = !isMyMessage && !isAdmin && !msg.read_datetime && !revealed[msg.id]
                        return (
                            <div
                                key={msg.id || index}
                                className={classes.messageWrapper}
                                style={{ alignItems: isMyMessage ? 'flex-start' : 'flex-end' }}
                            >
                                <Paper
                                    className={`${classes.messageBubble} ${isMyMessage ? classes.myMessage : classes.theirMessage}`}
                                    elevation={1}
                                >
                                    {!isMyMessage && (
                                        <Typography variant="caption" style={{ fontWeight: 'bold', display: 'block', marginBottom: 4 }}>
                                            {toFarsiDigits(msg.sender_name)}
                                        </Typography>
                                    )}

                                    {locked ? (
                                        <Box
                                            onClick={() => openMessage(msg.id)}
                                            title="برای مشاهده و ثبت زمان رویت کلیک کنید"
                                            style={{ cursor: 'pointer', position: 'relative' }}
                                        >
                                            <Box style={{
                                                filter: 'blur(4.5px)',
                                                opacity: .75,
                                                userSelect: 'none',
                                                pointerEvents: 'none',
                                            }}>
                                                <Typography variant="body2" style={{ whiteSpace: 'pre-wrap' }}>
                                                    {toFarsiDigits(msg.message)}
                                                </Typography>
                                            </Box>
                                            <Box style={{
                                                position: 'absolute', inset: 0,
                                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                            }}>
                                                <Typography variant="caption" style={{
                                                    background: 'rgba(25,118,210,.92)', color: '#fff',
                                                    borderRadius: 999, padding: '4px 12px', fontWeight: 700,
                                                }}>
                                                    🔒 خوانده‌نشده — برای مشاهده کلیک کنید
                                                </Typography>
                                            </Box>
                                        </Box>
                                    ) : (
                                        (() => {
                                            const { quote, body } = splitQuote(msg.message)
                                            return (
                                                <>
                                                    {quote && (
                                                        <Box style={{
                                                            background: isMyMessage ? 'rgba(255,255,255,.18)' : '#eef1fb',
                                                            borderRight: '4px solid ' + (isMyMessage ? 'rgba(255,255,255,.75)' : '#5c6bc0'),
                                                            borderRadius: 8,
                                                            padding: '7px 10px',
                                                            marginBottom: 8,
                                                            maxWidth: '100%',
                                                        }}>
                                                            <Typography style={{
                                                                fontSize: 11,
                                                                fontWeight: 800,
                                                                fontFamily: 'Tahoma, Arial, sans-serif',
                                                                color: isMyMessage ? 'rgba(255,255,255,.9)' : '#3949ab',
                                                                marginBottom: 3,
                                                            }}>
                                                                ↩ {toFarsiDigits(quote.name)}
                                                            </Typography>
                                                            <Typography style={{
                                                                fontSize: 11.5,
                                                                fontFamily: 'Tahoma, Arial, sans-serif',
                                                                fontStyle: 'italic',
                                                                lineHeight: 1.85,
                                                                whiteSpace: 'pre-wrap',
                                                                color: isMyMessage ? 'rgba(255,255,255,.88)' : '#5b6180',
                                                            }}>
                                                                {toFarsiDigits(quote.text)}
                                                            </Typography>
                                                        </Box>
                                                    )}
                                                    <Typography variant="body2" style={{ whiteSpace: 'pre-wrap' }}>
                                                        {toFarsiDigits(body)}
                                                    </Typography>
                                                </>
                                            )
                                        })()
                                    )}

                                    {msg.attachment && !locked && (
                                        <Box mt={1}>
                                            <Button
                                                size="small"
                                                startIcon={<GetAppIcon />}
                                                href={msg.attachment}
                                                target="_blank"
                                                download
                                                style={{
                                                    color: isMyMessage ? 'white' : '#1976d2',
                                                    textTransform: 'none',
                                                    fontSize: '0.75rem'
                                                }}
                                            >
                                                {msg.attachment_name || 'فایل'}
                                            </Button>
                                        </Box>
                                    )}

                                    {!locked && (() => {
                                        const m = /https?:\/\/[^\s"'<>]+\/u\/[A-Za-z0-9]+/.exec(msg.message || '')
                                        if (!m) return null
                                        return (
                                            <Box mt={1}>
                                                <Button
                                                    size="small"
                                                    onClick={() => setQrUrl(m[0])}
                                                    style={{
                                                        color: isMyMessage ? 'white' : '#0f766e',
                                                        textTransform: 'none',
                                                        fontSize: '0.75rem',
                                                        fontWeight: 700,
                                                        border: '1px solid ' + (isMyMessage ? 'rgba(255,255,255,.6)' : '#0f766e'),
                                                        borderRadius: 999,
                                                        padding: '2px 10px',
                                                        minWidth: 0,
                                                    }}
                                                >
                                                    ▦ QR گوشی
                                                </Button>
                                            </Box>
                                        )
                                    })()}
                                </Paper>

                                <Box
                                    style={{
                                        alignSelf: isMyMessage ? 'flex-start' : 'flex-end',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 6,
                                        flexWrap: 'wrap',
                                    }}
                                >
                                    <Typography className={classes.messageTime} style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
                                        <span>ارسال: {gregorianToJalali(msg.message_datetime)}</span>
                                        {msg.read_datetime && (
                                            <span style={{ color: '#2e7d32', fontWeight: 700 }}>
                                                رویت: {gregorianToJalali(msg.read_datetime)}
                                            </span>
                                        )}
                                    </Typography>
                                    {(() => {
                                        const st = STATUS_META[statusOf(msg, isMyMessage)] || STATUS_META.sent
                                        return (
                                            <span className={`${classes.statusChip} ${classes[st.cls]}`}>
                                                {st.label}
                                            </span>
                                        )
                                    })()}
                                    {!locked && (
                                        <>
                                            <Button
                                                size="small"
                                                className={classes.msgAction}
                                                startIcon={<FileCopyIcon style={{ fontSize: 13 }} />}
                                                onClick={() => copyMessage(msg.message)}
                                                title="کپی متن پیام"
                                            >
                                                کپی
                                            </Button>
                                            <Button
                                                size="small"
                                                className={classes.msgAction}
                                                startIcon={<ReplyIcon style={{ fontSize: 14 }} />}
                                                onClick={() => setReplyTo({
                                                    id: msg.id,
                                                    name: isMyMessage ? (userInfo?.name || 'خودم') : (msg.sender_name || 'کاربر'),
                                                    text: msg.message,
                                                })}
                                                title="پاسخ به همین پیام"
                                            >
                                                پاسخ
                                            </Button>
                                        </>
                                    )}
                                </Box>
                            </div>
                        )
                    })
                )}
                <div ref={chatEndRef} />
            </Paper>

            <Divider />

            {/* منطقه ارسال پیام جدید */}
            <Box>
                {replyTo && (
                    <Box className={classes.replyBar}>
                        <Box style={{ flex: 1, minWidth: 0 }}>
                            <Typography style={{ fontSize: 11, fontWeight: 800, color: '#283593' }}>
                                ↩ پاسخ به {replyTo.name}
                            </Typography>
                            <Typography style={{
                                fontSize: 11.5, color: '#5c6bc0', whiteSpace: 'nowrap',
                                overflow: 'hidden', textOverflow: 'ellipsis',
                            }}>
                                {String(replyTo.text || '').slice(0, 140)}
                            </Typography>
                        </Box>
                        <IconButton size="small" onClick={() => setReplyTo(null)} title="لغو پاسخ">
                            <CloseIcon style={{ fontSize: 16 }} />
                        </IconButton>
                    </Box>
                )}
                {fileName && (
                    <div className={classes.attachmentPreview}>
                        <Typography variant="caption">📎 {fileName}</Typography>
                        <Button size="small" onClick={() => { setAttachment(null); setFileName('') }}>
                            حذف
                        </Button>
                    </div>
                )}

                {subLocked && (
                    <Box style={{
                        display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap',
                        background: '#fff4f4', borderTop: '1px solid #ffcdd2',
                        padding: '8px 12px',
                    }}>
                        <Typography style={{ fontSize: 12.5, color: '#b71c1c', fontWeight: 700, flex: 1 }}>
                            برای ارسال پیام، ابتدا حق اشتراک را پرداخت کنید.
                        </Typography>
                        <Button size="small" variant="contained" color="primary"
                            onClick={() => window.location.assign('/')}
                            style={{ fontWeight: 700 }}>
                            پرداخت اشتراک
                        </Button>
                    </Box>
                )}
                <div className={classes.inputArea}
                    style={subLocked ? { opacity: .45, pointerEvents: 'none' } : undefined}>
                    <TextField
                        fullWidth
                        multiline
                        maxRows={4}
                        variant="outlined"
                        placeholder="پیام خود را بنویسید..."
                        value={newMessage}
                        onChange={(e) => setNewMessage(e.target.value)}
                        onKeyPress={handleKeyPress}
                        size="small"
                    />
                    <input
                        accept="image/*,.pdf,.doc,.docx"
                        style={{ display: 'none' }}
                        id="message-thread-attachment-file"
                        type="file"
                        onChange={handleFileChange}
                    />
                    <label htmlFor="message-thread-attachment-file">
                        <IconButton component="span" color="primary">
                            <AttachFileIcon />
                        </IconButton>
                    </label>
                    <IconButton
                        color="primary"
                        onClick={handleSendMessage}
                        disabled={!newMessage.trim() && !attachment}
                    >
                        <SendIcon />
                    </IconButton>
                </div>
            </Box>

            <Dialog open={!!qrUrl} onClose={() => setQrUrl('')} maxWidth="xs">
                <DialogTitle style={{ textAlign: 'center', fontSize: 15 }}>
                    اسکن با دوربین گوشی
                </DialogTitle>
                <DialogContent style={{ textAlign: 'center' }}>
                    {qrImg && (
                        <img src={qrImg} alt="QR" style={{ width: 260, height: 260 }} />
                    )}
                    <Typography style={{ fontSize: 11.5, color: '#6b7280', wordBreak: 'break-all' }}>
                        {qrUrl}
                    </Typography>
                </DialogContent>
            </Dialog>
        </div>
    )
}

export default MessageThread
